/**
 * Imperative confirm/prompt dialogs powered by Shadcn AlertDialog/Dialog primitives.
 *
 * Usage:
 *   const { confirm, prompt } = useConfirm();
 *
 *   if (await confirm({
 *       title: "Delete worksite?",
 *       description: "Employees won't be able to check in here anymore.",
 *       confirmLabel: "Delete",
 *       danger: true,
 *   })) {
 *       await api.delete(...);
 *   }
 *
 *   const reason = await prompt({
 *       title: "Reject this leave request",
 *       description: "Reason will be emailed to the employee.",
 *       placeholder: "e.g. Project deadline conflict",
 *       multiline: true,
 *   });
 *   if (reason !== null) doReject(reason);
 *
 * Wrap your app in <ConfirmProvider /> once (top-level in AppLayout) and you're done.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ConfirmContext = createContext(null);

export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
    return ctx;
}

export function ConfirmProvider({ children }) {
    const [confirmState, setConfirmState] = useState(null);
    const [promptState, setPromptState] = useState(null);
    const [promptValue, setPromptValue] = useState("");
    const promptInputRef = useRef(null);

    const confirm = useCallback((opts = {}) => new Promise((resolve) => {
        setConfirmState({ ...opts, _resolve: resolve });
    }), []);

    const prompt = useCallback((opts = {}) => new Promise((resolve) => {
        setPromptValue(opts.defaultValue || "");
        setPromptState({ ...opts, _resolve: resolve });
    }), []);

    const closeConfirm = (value) => {
        if (confirmState?._resolve) confirmState._resolve(value);
        setConfirmState(null);
    };
    const closePrompt = (value) => {
        if (promptState?._resolve) promptState._resolve(value);
        setPromptState(null);
        setPromptValue("");
    };

    useEffect(() => {
        if (promptState) {
            // Autofocus the input on open
            const t = setTimeout(() => promptInputRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [promptState]);

    return (
        <ConfirmContext.Provider value={{ confirm, prompt }}>
            {children}

            <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) closeConfirm(false); }}>
                <AlertDialogContent data-testid="confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmState?.title || "Are you sure?"}</AlertDialogTitle>
                        {confirmState?.description && (
                            <AlertDialogDescription>{confirmState.description}</AlertDialogDescription>
                        )}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => closeConfirm(false)} data-testid="confirm-cancel">
                            {confirmState?.cancelLabel || "Cancel"}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => closeConfirm(true)}
                            data-testid="confirm-ok"
                            className={confirmState?.danger
                                ? "bg-red-600 hover:bg-red-700 text-white"
                                : "bg-klein hover:bg-klein-500 text-white"}
                        >
                            {confirmState?.confirmLabel || "Confirm"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!promptState} onOpenChange={(o) => { if (!o) closePrompt(null); }}>
                <DialogContent data-testid="prompt-dialog">
                    <DialogHeader>
                        <DialogTitle>{promptState?.title || "Enter a value"}</DialogTitle>
                        {promptState?.description && (
                            <DialogDescription>{promptState.description}</DialogDescription>
                        )}
                    </DialogHeader>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (promptState?.required && !promptValue.trim()) return;
                            closePrompt(promptValue);
                        }}
                    >
                        {promptState?.multiline ? (
                            <textarea
                                ref={promptInputRef}
                                value={promptValue}
                                onChange={(e) => setPromptValue(e.target.value)}
                                placeholder={promptState?.placeholder || ""}
                                rows={4}
                                data-testid="prompt-input"
                                className="w-full border border-ink/20 px-3 py-2 text-sm focus:border-klein focus:outline-none"
                            />
                        ) : (
                            <input
                                ref={promptInputRef}
                                type={promptState?.inputType || "text"}
                                value={promptValue}
                                onChange={(e) => setPromptValue(e.target.value)}
                                placeholder={promptState?.placeholder || ""}
                                data-testid="prompt-input"
                                className="w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                            />
                        )}
                        <DialogFooter className="mt-5">
                            <button
                                type="button"
                                onClick={() => closePrompt(null)}
                                data-testid="prompt-cancel"
                                className="px-5 py-2.5 text-sm hover:bg-ink/5"
                            >
                                {promptState?.cancelLabel || "Cancel"}
                            </button>
                            <button
                                type="submit"
                                data-testid="prompt-ok"
                                disabled={promptState?.required && !promptValue.trim()}
                                className="bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein-500 disabled:opacity-50"
                            >
                                {promptState?.confirmLabel || "OK"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </ConfirmContext.Provider>
    );
}
